import { useId } from "react";
import { useLocale, useTranslations } from "next-intl";
import { IconCheck, IconLink, IconLoader2 } from "@tabler/icons-react";
import { Plugin } from "@/types/enums/Plugin";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useEventTimezone } from "@/providers/EventProvider";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { formatDateInTz, safeDisplayTimeZone } from "@/lib/utils/datetime-tz";
import {
  DETAIL_READ_ONLY_BOX,
  DETAIL_READ_ONLY_TEXT,
} from "../../Inputs/detailFieldChrome";
import { usePortalLinkCopy } from "../portal-link/usePortalLinkCopy";
import type { LayoutFieldCtx } from "./GuestLayoutFieldInput";

/** The cell's input-sized frame — FormInput's box, so the pill sits in the
 *  grid at the height of its neighbours. */
const FRAME =
  "flex items-center h-9 px-1.5 rounded-[10px] border outline-quaternary surface-primary min-w-0";
const PILL =
  "flex items-center gap-1.5 h-6 max-w-full min-w-0 px-2.5 rounded-full border outline-quaternary surface-secondary body-3-medium font-primary";

/**
 * The guest popup's "Portal link" cell (W3-3, PB-FB-L22) — the header action's
 * twin, placed wherever the admin put it in the field grid: one pill,
 * "Generate & copy", that mints this guest's portal link and puts it on the
 * clipboard, then says until when the link works.
 *
 * Deliberately no "Open": opening the portal from the cockpit would be logged
 * as the GUEST's activity (portal visit) — the reason the header has none
 * either.
 *
 * The gate is the header action's (Actions.tsx), so the two never disagree:
 * the ATTACHMENTS plugin, `shared-links:write` (the mint), `attachments:read`
 * (the link shows the guest's attachments) and `guests:read.qr-token` (for a
 * ticket-only event the portal's main content IS the scan token). Where the
 * header simply has no button, a CELL that an admin placed must not vanish
 * without a word — it says "—" and names the reason in small text under it
 * (visible on touch and to a sighted keyboard user, read by a screen reader;
 * the hover title stays). The plugin is checked first: without the plugin
 * there is nothing to have rights to.
 *
 * Not rendered at all where the header offers no action either: an archived
 * guest (the header shows "Unarchive" only) and the pending-request decision
 * surface (`ctx.portalLinkHidden`).
 *
 * In the builder canvas (no guest, no event id) it previews the resting pill —
 * a static picture, no button, no mint hook; with the plugin off it previews
 * the dash, which is what the popup will show.
 */
export function PortalLinkLayoutCell({
  label,
  ctx,
}: {
  label: string;
  ctx: LayoutFieldCtx;
}) {
  const t = useTranslations("guestDetails");
  const { can } = useCapabilitiesOptional();
  const labelId = useId();

  if (ctx.portalLinkHidden || ctx.guest?.archived) return null;

  const eventId = ctx.eventId;
  const guestId = ctx.guest?.guestId ?? ctx.currentGuestId;
  const live = !!eventId && !!guestId;
  const hasPlugin = !!ctx.plugins?.includes(Plugin.ATTACHMENTS);
  const mayMint =
    can("shared-links:write") &&
    can("attachments:read") &&
    can("guests:read.qr-token");
  // Canvas: the designer arranges the cell for EVERY viewer of the popup, so
  // the designer's own rights are no preview of anything — only the plugin is.
  const reason = !hasPlugin
    ? t("portalLinkNoPlugin")
    : live && !mayMint
      ? t("portalLinkNoRights")
      : null;

  return (
    <div className="flex flex-col space-y-1.5" data-portal-link-field>
      <p id={labelId} className="body-3-medium truncate">
        {label}
      </p>
      {reason !== null ? (
        <div
          title={reason}
          data-testid="guest-detail-portal-link-field-unavailable"
          className="flex flex-col space-y-1 min-w-0"
        >
          <div className={DETAIL_READ_ONLY_BOX}>
            <span aria-hidden className={`${DETAIL_READ_ONLY_TEXT} font-quaternary`}>
              —
            </span>
          </div>
          <p
            data-testid="guest-detail-portal-link-field-reason"
            className="body-4-regular font-tertiary"
          >
            {reason}
          </p>
        </div>
      ) : eventId && guestId ? (
        // Keyed by the guest: the copy state ("Copied · valid until …") is
        // ONE guest's — a popup that swaps the guest in place starts over.
        <PortalLinkMintPill key={guestId} eventId={eventId} guestId={guestId} labelId={labelId} />
      ) : (
        <div className={FRAME}>
          <span className={PILL}>
            <IconLink size={14} stroke={1.5} className="shrink-0" />
            <span className="truncate">{t("portalLinkGenerate")}</span>
          </span>
        </div>
      )}
    </div>
  );
}

function PortalLinkMintPill({
  eventId,
  guestId,
  labelId,
}: {
  eventId: string;
  guestId: string;
  labelId: string;
}) {
  const t = useTranslations("guestDetails");
  const tAttachments = useTranslations("attachments");
  const locale = useLocale();
  const timeZone = safeDisplayTimeZone(useEventTimezone());
  const { guard } = usePreviewWriteGuard();
  // No reset: "valid until <date>" stays readable while the popup is open. A
  // second click copies again — the backend hands the same caller the same
  // still-valid link, so the date does not move.
  const { state, expiresAt, busy, copy } = usePortalLinkCopy(eventId, guestId);
  const text =
    state === "copied"
      ? expiresAt
        ? t("portalLinkCopiedUntil", {
            date: formatDateInTz(expiresAt, locale, timeZone),
          })
        : tAttachments("linkCopied")
      : t("portalLinkGenerate");
  const Icon = busy ? IconLoader2 : state === "copied" ? IconCheck : IconLink;
  return (
    <div className={FRAME}>
      {/* Never `disabled` while minting: a disabled button drops keyboard focus
          onto <body>, and the "copied" swap would happen on an element the
          reader has left. The shared lock is the double-submit guard; the
          spinner only reports. Through `guard()`: in a role preview the click
          explains instead of minting. */}
      <button
        type="button"
        data-testid="guest-detail-portal-link-field"
        aria-describedby={labelId}
        aria-busy={busy}
        onClick={guard(copy)}
        className={`${PILL} cursor-pointer hover:surface-tertiary transition-colors`}
      >
        <Icon
          size={14}
          stroke={1.5}
          className={`shrink-0 ${busy ? "animate-spin" : ""}`}
        />
        <span className="truncate">{text}</span>
      </button>
    </div>
  );
}
